/**
 * HTTP client for `/api/v1`.
 *
 * Three things this does that the fork's version did not:
 *   1. unwraps the `{error:{code,message,details}}` envelope into a typed
 *      `ApiError`, so callers can branch on a stable machine string rather than
 *      pattern-matching prose;
 *   2. routes a 401 to the console's own /login route instead of a server page;
 *   3. carries the session cookie on every call (`credentials: "include"`).
 */

export const API_BASE = "/api/v1";

/** Stable machine codes from rest-api.md. Unknown codes still parse. */
export type ApiErrorCode =
  | "not_found"
  | "forbidden"
  | "conflict"
  | "validation_error"
  | "device_offline"
  | "run_in_progress"
  | "lease_held"
  | "blocked_by_gate"
  | "unauthorized"
  | "network"
  | (string & {});

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: ApiErrorCode,
    message: string,
    public details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** The browser could not reach the server at all — not a server verdict. */
  get isTransport(): boolean {
    return this.status === 0;
  }
}

/**
 * Set by the app shell so a 401 lands on the login route with a `next` param
 * rather than blowing the SPA away with a full page load. Falls back to
 * `location.assign` before the router has mounted.
 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

let sawUnauthorized = false;

function handleUnauthorized() {
  // Only redirect once per 401 storm: eight widgets failing together must not
  // push eight history entries.
  if (sawUnauthorized) return;
  sawUnauthorized = true;
  window.setTimeout(() => {
    sawUnauthorized = false;
  }, 2000);
  if (onUnauthorized) onUnauthorized();
  else if (!window.location.pathname.startsWith("/login")) {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.assign(`/login?next=${next}`);
  }
}

async function parseError(r: Response): Promise<ApiError> {
  let code: ApiErrorCode = `http_${r.status}`;
  let message = `HTTP ${r.status}`;
  let details: Record<string, unknown> = {};
  try {
    const body = await r.json();
    const e = (body as { error?: { code?: string; message?: string; details?: Record<string, unknown> } })
      ?.error;
    if (e) {
      code = e.code ?? code;
      message = e.message ?? message;
      details = e.details ?? {};
    } else if (typeof body === "string") {
      message = body;
    }
  } catch {
    // Non-JSON body (nginx 502 page, a proxy timeout). Keep the status message.
  }
  return new ApiError(r.status, code, message, details);
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Skip the JSON body/Accept dance — used by the blob helpers. */
  raw?: boolean;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, raw, headers, ...rest } = opts;
  let r: Response;
  try {
    r = await fetch(API_BASE + path, {
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...(headers ?? {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...rest,
    });
  } catch (e) {
    // fetch only rejects on a transport failure — DNS, offline, CORS, aborted.
    throw new ApiError(0, "network", e instanceof Error ? e.message : "network error");
  }

  if (r.status === 401) {
    handleUnauthorized();
    throw new ApiError(401, "unauthorized", "session expired");
  }
  if (!r.ok) throw await parseError(r);
  if (r.status === 204) return undefined as T;
  if (raw) return r as unknown as T;

  const ct = r.headers.get("content-type") ?? "";
  if (!ct.includes("json")) return (await r.text()) as unknown as T;
  return (await r.json()) as T;
}

export function get<T = unknown>(path: string, init?: RequestOptions) {
  return request<T>(path, { method: "GET", ...init });
}
export function post<T = unknown>(path: string, body?: unknown, init?: RequestOptions) {
  return request<T>(path, { method: "POST", body, ...init });
}
export function put<T = unknown>(path: string, body?: unknown, init?: RequestOptions) {
  return request<T>(path, { method: "PUT", body, ...init });
}
export function patch<T = unknown>(path: string, body?: unknown, init?: RequestOptions) {
  return request<T>(path, { method: "PATCH", body, ...init });
}
export function del<T = unknown>(path: string, init?: RequestOptions) {
  return request<T>(path, { method: "DELETE", ...init });
}

export const api = get;

/** Build a query string, dropping undefined/null/"" so filters stay tidy. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/** Console WebSocket URL, origin-relative so it works behind nginx. */
export function consoleSocketUrl(): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws/console`;
}

/** Screenshot URL for a step — served directly, never fetched into JS. */
export function stepScreenshotUrl(runId: string, n: number): string {
  return `${API_BASE}/runs/${encodeURIComponent(runId)}/steps/${n}/screenshot`;
}

/** True when the failure means "the browser lost the server", not "you may not". */
export function isConnectivityError(e: unknown): boolean {
  return e instanceof ApiError && (e.isTransport || e.status === 502 || e.status === 503 || e.status === 504);
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}
